import type { MatterStatus } from '@shared/types';
import type { BackendRouteDefinition } from '../types';

// Flipped from `transport: 'callable'` to `transport: 'api'` by PRA-825 (the
// Phase 6 PRE-1 prerequisite). The handler now lives at
// `services/api-longrun-node/src/routes/plugins/generateMatterFollowUpEmailDraft.ts`
// — placement on `api-longrun-node` matches the OpenAI-flavored sibling
// routes (`openaiChat`, `generateDocument`) that Phase 5 sent there for the
// same reasons: 30 s timeout + `OPENAI_API_KEY` secret + max-tokens budget
// that does not belong in the fast-auth pool on `api-core-node`.
//
// `path` is the route's leaf — `apiBaseUrl` (see
// `shared/backend/config.ts` -> `resolveApiBaseUrl`) contributes the `/api`
// prefix at invoke time. A leading `/api/` in `path` would produce
// `/api/api/firms/...` and miss the Vite dev proxy / Hosting rewrite target.
//
// The wire shape on the new HTTP route matches the firms-app's request data
// (`{ matter: { id, name, status, ... } }`), so the body mapper is a pure
// pass-through — the typecheck flags a future shape drift between the two
// ends instead of letting it slip into a silent runtime failure.

// The cloudFunctions file these types replaced used a `namespace`-grouped
// pair. The firms-app eslint config bans namespaces in apps/firms/src/
// (`@typescript-eslint/no-namespace`); declare each type at the module top
// level instead and pair them by a shared `GenerateMatterFollowUpEmailDraft`
// prefix so call sites stay grep-friendly.
export interface GenerateMatterFollowUpEmailDraftRequestData {
  matter: {
    id: string;
    name: string;
    status: MatterStatus;
    description?: string;
    scope?: string;
    matterType?: string;
    state?: string;
    primaryClientName?: string;
    schedulingEmailSentAt?: string;
    engagementLetterSentAt?: string;
  };
}

export interface GenerateMatterFollowUpEmailDraftResult {
  subject: string;
  body: string;
}

export const generateMatterFollowUpEmailDraftRoute = {
  transport: 'api',
  method: 'POST',
  path: '/firms/matters/follow-up-email-drafts',
  body: (requestData: GenerateMatterFollowUpEmailDraftRequestData) =>
    requestData,
} as const satisfies BackendRouteDefinition;
